import React, { useEffect, useMemo, useReducer, useRef } from 'react';
import styled from 'styled-components';
import { Tooltip } from 'react-tooltip';

import ArrowDown from '@src/svgs/ArrowDown';
import RotatingSquare from '@src/svgs/RotatingSquare';
import { convertTimeToMMSS } from '@src/utility/timeFormat';

import { tryParsePartialOutline } from './outlineParser';

import type { ReelOutline, ReelChapter } from './types';

// --- Sub-components ---

type ChapterAccordionProps = {
  chapter: ReelChapter;
  chapterIndex: number;
  defaultOpen: boolean;
};

const ChapterAccordion = ({
  chapter,
  chapterIndex,
  defaultOpen,
}: ChapterAccordionProps) => {
  const [isOpen, toggleOpen] = useReducer(
    (state: boolean) => !state,
    defaultOpen,
  );

  const chapterDuration = chapter.beats.reduce(
    (sum, beat) => sum + beat.estimatedDuration,
    0,
  );
  const beatCount = chapter.beats.length;
  const beatLabel = beatCount === 1 ? '1 beat' : `${beatCount} beats`;
  const uniqueSpeakers = new Set(
    chapter.beats.map(b => b.speaker).filter(Boolean),
  );
  const speakerCount = uniqueSpeakers.size;
  const speakerLabel =
    speakerCount === 0
      ? null
      : speakerCount === 1
        ? '1 speaker'
        : `${speakerCount} speakers`;

  return (
    <ChapterContainer>
      <ChapterHeader onClick={toggleOpen}>
        <ChapterLeft>
          <ChapterTime>{convertTimeToMMSS(chapterDuration)}</ChapterTime>
          <ChapterArrow isOpen={isOpen}>
            <ArrowDown width="10" strokeColor="var(--foreground)" />
          </ChapterArrow>
          <ChapterTitle>
            Ch {chapterIndex + 1}: {chapter.title}
          </ChapterTitle>
        </ChapterLeft>
        <ChapterRight>
          <ChapterMeta>{beatLabel}</ChapterMeta>
          {speakerLabel && <ChapterMeta>&middot; {speakerLabel}</ChapterMeta>}
        </ChapterRight>
      </ChapterHeader>
      {isOpen && (
        <BeatsContainer>
          {chapter.beats.map((beat, beatIndex) => (
            <BeatRow
              key={beatIndex}
              data-tooltip-id="beat-tooltip"
              data-tooltip-content={beat.description}
              data-tooltip-delay-show={150}
            >
              <BeatTime>
                {convertTimeToMMSS(beat.estimatedDuration)}
              </BeatTime>
              <BeatTitle>{beat.shortTitle || beat.description}</BeatTitle>
              {beat.speaker && (
                <BeatSpeaker
                  data-tooltip-id="beat-tooltip"
                  data-tooltip-content={beat.speaker}
                  data-tooltip-delay-show={150}
                >
                  {beat.speaker}
                </BeatSpeaker>
              )}
            </BeatRow>
          ))}
        </BeatsContainer>
      )}
    </ChapterContainer>
  );
};

// --- Main component ---

type OutlineViewerPanelProps = {
  outline: ReelOutline | null;
  streamingText?: string;
  isOutlineLoading?: boolean;
  onApprove?: () => void;
  onReprocess?: () => void;
  canApprove?: boolean;
  canReprocess?: boolean;
  isProcessing?: boolean;
};

const OutlineViewerPanel = ({
  outline,
  streamingText,
  isOutlineLoading,
  onApprove,
  onReprocess,
  canApprove,
  canReprocess,
  isProcessing,
}: OutlineViewerPanelProps) => {
  // Try to parse partial outline from streaming text.
  // Cache the last successful parse so we don't flash back to empty
  // when a chunk lands mid-property and the parse temporarily fails.
  const lastGoodPartialRef = useRef<ReelOutline | null>(null);

  const parsedPartial = useMemo(() => {
    if (!streamingText) return null;
    return tryParsePartialOutline(streamingText);
  }, [streamingText]);

  // Cache the last successful parse so we don't flash back to empty
  // when a chunk lands mid-property and the parse temporarily fails.
  useEffect(() => {
    if (!streamingText) {
      lastGoodPartialRef.current = null;
    } else if (parsedPartial) {
      lastGoodPartialRef.current = parsedPartial;
    }
  }, [streamingText, parsedPartial]);
  const partialOutline = streamingText ? (parsedPartial ?? lastGoodPartialRef.current) : null;

  // During streaming, prefer the partial outline (shows progressive updates)
  // Once streaming ends, use the final outline
  const displayOutline = streamingText ? (partialOutline ?? outline) : outline;

  if (!displayOutline && streamingText) {
    // Streaming but can't parse yet — show loading
    return (
      <PanelContainer>
        <EmptyState>
          Building outline...
        </EmptyState>
      </PanelContainer>
    );
  }

  if (!displayOutline) {
    return (
      <PanelContainer>
        <EmptyState>
          No outline generated yet. Use the chat to create one.
        </EmptyState>
      </PanelContainer>
    );
  }

  return (
    <PanelContainer>
      <ScrollableArea>
        {displayOutline.chapters.map((chapter, index) => (
          <ChapterAccordion
            key={`ch-${chapter.order}-${index}`}
            chapter={chapter}
            chapterIndex={index}
            defaultOpen={true}
          />
        ))}
      </ScrollableArea>
      <Tooltip
        id="beat-tooltip"
        place="bottom"
        style={{
          maxWidth: '400px',
          fontSize: '12px',
          lineHeight: '1.5',
          zIndex: 100001,
          borderRadius: '6px',
          padding: '8px 12px',
        }}
      />
      {isOutlineLoading && (
        <OutlineActionBar>
          <StreamingIndicator>
            <RotatingSquare strokeColor="#a0a0a0" />
            <span>Generating outline<span className="loading-dots" /></span>
          </StreamingIndicator>
        </OutlineActionBar>
      )}
      {!isOutlineLoading && (canApprove || canReprocess) && (
        <OutlineActionBar>
          <ActionRow>
            {canApprove && onApprove && (
              <OutlineActionButton onClick={onApprove} disabled={isProcessing}>
                {isProcessing ? 'Processing...' : 'Approve Outline'}
              </OutlineActionButton>
            )}
            {canReprocess && onReprocess && (
              <OutlineActionButton onClick={onReprocess} disabled={isProcessing} $secondary>
                {isProcessing ? 'Reprocessing...' : 'Reprocess Clips'}
              </OutlineActionButton>
            )}
          </ActionRow>
        </OutlineActionBar>
      )}
    </PanelContainer>
  );
};

export default OutlineViewerPanel;

// --- Styled components ---

const PanelContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background: var(--background);
  padding: 12px 16px;
`;

const ScrollableArea = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 6px;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const EmptyState = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  color: var(--text-muted);
  font-size: var(--text-xs);
  padding: 16px;
`;

const StreamingIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: var(--text-secondary);
  font-size: 13px;
  width: 100%;

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  svg {
    width: 16px;
    height: 16px;
    animation: spin 1.5s linear infinite;
  }

  @keyframes loading-dots {
    0% { content: ''; }
    25% { content: '.'; }
    50% { content: '..'; }
    75% { content: '...'; }
  }

  .loading-dots::after {
    content: '';
    animation: loading-dots 1.5s steps(1) infinite;
  }
`;

const ChapterContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const ChapterHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  padding: 4px 0;

  &:hover {
    opacity: 0.8;
  }
`;

const ChapterLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const ChapterTime = styled.span`
  font-size: 11px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
`;

const ChapterArrow = styled.span<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: var(--transition-normal);
  transform: ${({ isOpen }) => (isOpen ? 'rotate(0deg)' : 'rotate(-90deg)')};

  > svg {
    color: var(--foreground);
  }
`;

const ChapterTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: var(--foreground);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChapterRight = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const ChapterMeta = styled.span`
  font-size: 10px;
  color: var(--text-muted);
  white-space: nowrap;
`;

const BeatsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
  margin-left: 18px;
  margin-bottom: 4px;
`;

const BeatRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  line-height: 1.4;
  background: var(--surface-elevated, rgba(255, 255, 255, 0.04));

  &:hover {
    background: var(--sidebar-hover, rgba(255, 255, 255, 0.06));
  }
`;

const BeatTime = styled.span`
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  flex-shrink: 0;
  font-size: 11px;
`;

const BeatTitle = styled.span`
  color: var(--text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
  flex: 1;
`;

const BeatSpeaker = styled.span`
  color: var(--accent);
  font-size: 11px;
  flex-shrink: 0;
  white-space: nowrap;
  text-align: right;
`;

const OutlineActionBar = styled.div`
  flex-shrink: 0;
  padding: 12px 16px 16px;
  display: flex;
  justify-content: center;
  align-items: center;
  border-top: 1px solid var(--border);
`;

const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
`;

const OutlineActionButton = styled.button<{ disabled?: boolean; $secondary?: boolean }>`
  display: flex;
  height: 32px;
  padding: 0 16px;
  justify-content: center;
  align-items: center;
  border-radius: 6px;
  border: ${props => props.$secondary ? '1px solid var(--border)' : 'none'};
  background: ${props => props.$secondary ? 'transparent' : 'var(--primary)'};
  color: ${props => props.$secondary ? 'var(--text-secondary)' : 'var(--background)'};
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  transition: all 150ms ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;

